/**
 * CatalogSelect — the shared catalog-driven <select> for the Wizard and TemplateEditor Model/Style
 * pickers (Task F5 item 6). The "Couldn't load — check connection" fallback + the option-mapping +
 * the keep-the-current-value-selectable logic were duplicated 4× (editor Model/Style, wizard
 * Model/Style) and had DRIFTED: the editor preserved the currently-selected value on a failed fetch
 * but the wizard dropped it. Consolidating here fixes that divergence — BOTH now keep the current
 * value selectable so a saved model/style is never silently lost when the catalog fetch fails.
 *
 * The InlineRetry affordance stays at each call site (it lives in the field's <label>, whose markup
 * differs between the two surfaces); this component owns only the <select> + its options.
 */

/** One catalog option (a phone model or a case style) reduced to what the <option> needs. */
export interface CatalogOption {
  value: string;
  label: string;
}

export interface CatalogSelectProps {
  id?: string;
  className?: string;
  value: string;
  onChange: (value: string) => void;
  /** data-testid for the <select>. */
  testId?: string;
  options: CatalogOption[];
  /** True when the catalog fetch failed. Combined with an EMPTY option list it swaps the dropdown
   *  to a single disabled "Couldn't load" option (plus the current value, kept selectable). When
   *  the fetch failed but stale options are still present, the normal list is shown. */
  error?: boolean;
  /** Leading empty-option label in the normal branch (default "—"). */
  placeholder?: string;
  required?: boolean;
  invalid?: boolean;
  describedBy?: string;
}

/** A catalog <select> with the standard couldn't-load fallback. On error (and no loaded options)
 *  it shows the disabled notice but ALWAYS keeps the current value selectable so a saved value
 *  survives a failed fetch; otherwise it renders the placeholder + options (+ the current value if
 *  it isn't in the fetched list, so an unknown-but-saved id still shows). */
export function CatalogSelect({
  id,
  className = "input",
  value,
  onChange,
  testId,
  options,
  error = false,
  placeholder = "—",
  required = false, invalid = false, describedBy,
}: CatalogSelectProps) {
  const showError = error && options.length === 0;
  return (
    <select id={id} className={className} value={value} onChange={(e) => onChange(e.target.value)}
      aria-required={required || undefined} aria-invalid={invalid || showError || undefined}
      aria-describedby={describedBy} data-testid={testId}>
      {showError ? (
        <>
          <option value="" disabled>Couldn't load — check connection</option>
          {/* Keep the current value selectable so the saved model/style isn't dropped on a failed fetch. */}
          {value && <option value={value}>{value}</option>}
        </>
      ) : (
        <>
          <option value="">{placeholder}</option>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          {value && !options.some((o) => o.value === value) && (
            <option value={value}>{value}</option>
          )}
        </>
      )}
    </select>
  );
}
